import React from 'react';

export type TypewriterOptions = {
  /** Per-character cadence while typing in a phrase. */
  typeMs: number;
  /** Per-character cadence while deleting a phrase (usually faster than typing). */
  deleteMs: number;
  /** Hold a fully-typed phrase before deleting it. */
  holdMs: number;
  /** Pause after a phrase is fully deleted before the next one starts typing. */
  gapMs: number;
  /** Reduced motion: skip per-character typing; the full phrases simply rotate. */
  reduced?: boolean;
};

export type TypewriterState = {
  /** The visible substring of the current phrase (the full phrase under reduced motion). */
  text: string;
  /** Index of the phrase currently on screen (drives the caller's cross-fade key). */
  index: number;
  /** True while characters are being added or removed (the caret holds steady, then blinks). */
  busy: boolean;
};

type Phase = 'typing' | 'holding' | 'deleting' | 'gap';

/** A ±~35% cadence jitter so the typing feels human rather than metronomic. */
const jitter = (ms: number): number => ms * (0.82 + Math.random() * 0.46);

/**
 * Drives a looping typewriter over a list of phrases: type one in character by
 * character, hold it, delete it, pause, then move to the next (wrapping forever).
 * One `setTimeout` is scheduled per step and cleared on unmount/re-render, so it
 * never stacks timers. Under reduced motion it degrades to rotating whole phrases
 * on a calm cadence (the caller cross-fades them), with no per-character motion.
 *
 * The phrases are read through a ref inside the scheduler, so re-passing a freshly
 * built array each render (e.g. `[t('…'), …]`) does not churn the timer; the
 * visible text is still derived from the live prop, so a language switch updates
 * the wording immediately.
 */
export const useTypewriter = (
  phrases: string[],
  { typeMs, deleteMs, holdMs, gapMs, reduced = false }: TypewriterOptions,
): TypewriterState => {
  const phrasesRef = React.useRef(phrases);
  phrasesRef.current = phrases;
  const count = phrases.length;

  const [index, setIndex] = React.useState(0);
  const [sub, setSub] = React.useState(0);
  const [phase, setPhase] = React.useState<Phase>('typing');

  React.useEffect(() => {
    if (count === 0) {
      return undefined;
    }

    // Reduced motion: rotate whole phrases on a calm cadence (the caller fades them).
    if (reduced) {
      const id = window.setTimeout(() => setIndex((i) => (i + 1) % count), holdMs + gapMs);
      return () => window.clearTimeout(id);
    }

    const full = phrasesRef.current[index] ?? '';
    let id: number;
    if (phase === 'typing') {
      id =
        sub < full.length
          ? window.setTimeout(() => setSub((s) => s + 1), jitter(typeMs))
          : window.setTimeout(() => setPhase('holding'), 0);
    } else if (phase === 'holding') {
      id = window.setTimeout(() => setPhase('deleting'), holdMs);
    } else if (phase === 'deleting') {
      id =
        sub > 0
          ? window.setTimeout(() => setSub((s) => s - 1), jitter(deleteMs))
          : window.setTimeout(() => setPhase('gap'), 0);
    } else {
      id = window.setTimeout(() => {
        setIndex((i) => (i + 1) % count);
        setPhase('typing');
      }, gapMs);
    }
    return () => window.clearTimeout(id);
  }, [phase, sub, index, reduced, count, typeMs, deleteMs, holdMs, gapMs]);

  const current = phrasesRef.current[index] ?? '';
  const text = reduced ? current : current.slice(0, sub);
  const busy = !reduced && (phase === 'typing' || phase === 'deleting');
  return { text, index, busy };
};

export default useTypewriter;
